import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs';
import { Question } from 'src/app/core/models/questionnaire.model';
import { DocumentUploadModalComponent } from '../document-upload-modal/document-upload-modal.component';
import { QuestionAnswer } from '../question/question.component';

@Component({
  selector: 'app-question-multi-file',
  templateUrl: './question-multi-file.component.html',
  styleUrls: ['./question-multi-file.component.scss'],
})
export class QuestionMultiFileComponent implements OnInit {
  @Input('question') question!: Question;
  @Input('context') context!: QuestionContext;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  dialogData?: {
    bucket: string;
    name: string;
    type: 'PERSONAL' | 'CLAIM';
    multiple: boolean;
    ref: string;
    maxFileSize?: number;
    maxFiles?: number;
    question_id?: string;
    allowedTypes?: string[];
    accept?: string;
    acceptedFormatsLabel?: string;
  };

  filesLength: number = 0;

  private readonly allowedDocumentTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'application/xml',
    'text/xml',
    'application/json',
  ];

  private readonly allowedImageTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/tiff',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/svg+xml',
    'image/avif',
  ];

  private readonly acceptedExtensions = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.txt',
    '.csv',
    '.rtf',
    '.odt',
    '.ods',
    '.odp',
    '.xml',
    '.json',
    '.jpg',
    '.jpeg',
    '.jpe',
    '.jfif',
    '.png',
    '.gif',
    '.bmp',
    '.tif',
    '.tiff',
    '.webp',
    '.heic',
    '.heif',
    '.svg',
    '.avif',
  ];

  private readonly acceptedFormatsLabel =
    'PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, RTF, ODT, ODS, ODP, XML, JSON, JPG, JPEG, PNG, GIF, BMP, TIFF, WEBP, HEIC, HEIF, SVG, AVIF';

  constructor(public dialog: MatDialog) {}

  ngOnInit(): void {
    this.filesLength =
      (this.question.answer?.value as unknown as string[])?.length ?? 0;
  }

  change(ev: QuestionAnswer) {
    this.changeAnswer.next(ev);
  }

  openModal() {
    const limit = this.question.configuration?.limit;
    this.dialogData = {
      bucket: this.question.configuration.bucket,
      name: this.question.slug,
      multiple: limit > 1,
      ref: this.context.claim_id!,
      // provide the question id so uploads can be associated to the question
      question_id: this.question.id,
      type: 'CLAIM',
      maxFileSize: this.question.configuration?.maxFileSize,
      maxFiles: typeof limit === 'number' && limit > 0 ? limit : 1,
      allowedTypes: [...this.allowedDocumentTypes, ...this.allowedImageTypes],
      accept: this.acceptedExtensions.join(','),
      acceptedFormatsLabel: this.acceptedFormatsLabel,
    };

    const dialogRef = this.dialog.open(DocumentUploadModalComponent, {
      data: this.dialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(filter((e) => !!e))
      .subscribe((result) => {
        const parsed = JSON.parse(result);
        if (parsed.status == 'COMPLETED') {
          this.question.isValid = this.checkValid(parsed.value);
          this.question.answer = { value: parsed.value };
          this.filesLength = parsed?.value?.length ?? 0;

          this.change({
            answer: { value: parsed.value },
            question: this.question,
          });
        } else {
          this.filesLength = 0;
        }
      });
  }

  checkValid(value?: string): boolean {
    if (!this.question.configuration?.required && !value) {
      return true;
    }

    if (this.question.configuration.required && !value) {
      return false;
    }

    return true;
  }
}

export interface QuestionContext {
  claim_id: string;
  index?: number;
}
